export interface FixedRegionLayer {
  id: string;
  src: string;
  x: number;
  y: number;
  depth: number;
}

export interface YSortedRegionProp {
  id: string;
  src: string;
  x: number;
  y: number;
  anchorX: number;
  anchorY: number;
  width?: number;
  height?: number;
}

export interface LayeredRegionBundle {
  id: string;
  width: 1280;
  height: 960;
  base: string;
  collision: string;
  zones: string;
  hooks: string;
  belowActors: readonly FixedRegionLayer[];
  ySortedProps: readonly YSortedRegionProp[];
  aboveActors: readonly FixedRegionLayer[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function fixedLayer(value: unknown): FixedRegionLayer | null {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.id) ||
    !isNonEmptyString(value.src) ||
    !isFiniteNumber(value.x) ||
    !isFiniteNumber(value.y) ||
    !isFiniteNumber(value.depth)
  ) return null;
  return { id: value.id, src: value.src, x: value.x, y: value.y, depth: value.depth };
}

function ySortedProp(value: unknown): YSortedRegionProp | null {
  const hasWidth = isRecord(value) && value.width !== undefined;
  const hasHeight = isRecord(value) && value.height !== undefined;
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.id) ||
    !isNonEmptyString(value.src) ||
    !isFiniteNumber(value.x) ||
    !isFiniteNumber(value.y) ||
    !isFiniteNumber(value.anchorX) ||
    value.anchorX < 0 ||
    value.anchorX > 1 ||
    !isFiniteNumber(value.anchorY) ||
    value.anchorY < 0 ||
    value.anchorY > 1 ||
    hasWidth !== hasHeight ||
    (hasWidth && (!isFiniteNumber(value.width) || value.width <= 0)) ||
    (hasHeight && (!isFiniteNumber(value.height) || value.height <= 0))
  ) return null;

  const prop: YSortedRegionProp = {
    id: value.id,
    src: value.src,
    x: value.x,
    y: value.y,
    anchorX: value.anchorX,
    anchorY: value.anchorY,
  };
  if (hasWidth && hasHeight) {
    prop.width = value.width as number;
    prop.height = value.height as number;
  }
  return prop;
}

function parseList<T>(value: unknown, parse: (entry: unknown) => T | null): T[] | null {
  if (value === undefined) return [];
  if (!Array.isArray(value)) return null;
  const parsed = value.map(parse);
  return parsed.every((entry): entry is T => entry !== null) ? parsed : null;
}

export function validateRegionAssetBundle(value: unknown): LayeredRegionBundle | null {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.id) ||
    value.width !== 1280 ||
    value.height !== 960 ||
    !isNonEmptyString(value.base) ||
    !isNonEmptyString(value.collision) ||
    !isNonEmptyString(value.zones) ||
    !isNonEmptyString(value.hooks)
  ) return null;

  const belowActors = parseList(value.belowActors, fixedLayer);
  const ySortedProps = parseList(value.ySortedProps, ySortedProp);
  const aboveActors = parseList(value.aboveActors, fixedLayer);
  if (!belowActors || !ySortedProps || !aboveActors) return null;

  const ids = [...belowActors, ...ySortedProps, ...aboveActors].map((entry) => entry.id);
  if (new Set(ids).size !== ids.length) return null;

  return {
    id: value.id,
    width: 1280,
    height: 960,
    base: value.base,
    collision: value.collision,
    zones: value.zones,
    hooks: value.hooks,
    belowActors,
    ySortedProps,
    aboveActors,
  };
}
